import React, { useState } from 'react';
import { BarList, Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput, panelPost, useAdminGet } from '../kit';
import { Modal } from '../email/emailKit';
import { ORIGIN_LABEL, OriginPill, TAGS_SECTION, invalidateTagOptions } from './shared';

/**
 * User tags → Account origin (owner, 2026-10-01: "give an option in the admin
 * panel in case we want to edit this").
 *
 * Where each account came from — a self sign-up or added by the team — is
 * recorded as accounts are made and worked out for older ones (Backend/
 * account_origin.py; DOCS/ACCOUNT_ORIGIN_AND_BULK_ADDS.md). This screen is
 * where that is corrected:
 *  · Bulk-add batches: runs of accounts made minutes apart. The owner
 *    confirmed the past ones (2026-10-01); a new run shows as "detected" until
 *    someone confirms it or says it was not a bulk add. A batch's accounts
 *    can also be set to an origin all at once.
 *  · Overrides: one account's origin set by hand (or a whole batch's), which
 *    wins over everything else; removing it goes back to automatic.
 */

type Batch = {
  id: number | null; started_at: string; ended_at: string; accounts: number; accounts_now: number;
  signed_in: number; overridden: number; status: 'confirmed' | 'rejected' | 'detected'; label: string; note: string;
  source: string; updated_by?: string; updated_at?: string; detected: boolean;
};
type Override = { user_id: string; email: string; origin: string; note: string; batch_id: number | null; set_by: string; set_at: string };

const istFmt = (iso: string, withDate = true) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB', {
    ...(withDate ? { day: 'numeric', month: 'short', year: 'numeric' } : {}),
    hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
  });
};
const STATUS: Record<string, { label: string; cls: string }> = {
  confirmed: { label: 'Bulk add', cls: 'text-green-700 border-green-300 bg-green-50' },
  rejected: { label: 'Not a bulk add', cls: 'text-grayscale-1200 border-grayscale-400 bg-grayscale-200' },
  detected: { label: 'Detected — review', cls: 'text-amber-800 border-amber-300 bg-amber-50' },
};

const OriginAdmin: React.FC = () => {
  const [tick, setTick] = useState(0);
  const q = useAdminGet<any>(TAGS_SECTION, 'origin_overview', {}, [tick]);
  const d = q.data;
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState<Batch | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editNote, setEditNote] = useState('');
  const [one, setOne] = useState({ email: '', origin: 'self', note: '' });

  const after = (text: string) => { setMsg({ kind: 'ok', text }); setTick(t => t + 1); invalidateTagOptions(); };
  const post = async (action: string, body: Record<string, any>, okText: string) => {
    setBusy(true); setMsg(null);
    const { error } = await panelPost(TAGS_SECTION, action, body);
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return false; }
    after(okText);
    return true;
  };
  // A flip sends only the status: the server re-words its own automatic
  // label and the seeded verdict, and keeps anything written by hand.
  const setStatus = (b: Batch, status: 'confirmed' | 'rejected') => post('origin_batch_save',
    b.id ? { id: b.id, status }
      : { started_at: b.started_at, ended_at: b.ended_at, status, note: '' },
    status === 'confirmed' ? 'Marked as a bulk add.' : 'Marked as not a bulk add.');
  const setBatchOrigin = async (b: Batch, origin: string) => {
    if (!b.id) { setMsg({ kind: 'error', text: 'Confirm or reject the batch first, then set its accounts.' }); return; }
    const what = origin ? `“${ORIGIN_LABEL[origin]}”` : 'automatic (remove the overrides)';
    if (!window.confirm(`Set all ${b.accounts_now.toLocaleString()} accounts made in this batch to ${what}?`)) return;
    await post('origin_batch_accounts', { id: b.id, origin, note: origin ? `Set for the whole batch “${b.label}”` : '' },
      origin ? `${b.accounts_now.toLocaleString()} accounts set to ${ORIGIN_LABEL[origin]}.` : 'Overrides removed; back to automatic.');
  };

  if (q.loading && !d) return <Loading />;
  if (q.error) return <Msg kind="error">{q.error}</Msg>;
  if (!d) return null;
  const batches: Batch[] = d.batches || [];
  const overrides: Override[] = d.overrides || [];
  const pending = batches.filter(b => b.status === 'detected').length;

  return (
    <div className="flex flex-col gap-4">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <KPIRow>
        {(d.origins || []).map((o: any) => (
          <KPI key={o.key} label={o.label} value={Number(o.n).toLocaleString()} hint={`of ${Number(d.total).toLocaleString()} accounts (team included)`} />
        ))}
        <KPI label="Bulk-add batches" value={batches.filter(b => b.status === 'confirmed').length}
          hint={pending ? `${pending} new run(s) to review` : 'all reviewed'} />
        <KPI label="Set by hand" value={overrides.length.toLocaleString()} hint="accounts with an override" />
      </KPIRow>

      <Section title="How accounts were made">
        <BarList items={(d.via || []).map((v: any) => ({ label: `${v.label}${v.origin === 'self' ? ' (self)' : v.origin === 'admin' ? ' (admin)' : ''}`, value: v.n }))} />
        <div className="text-[11px] text-grayscale-1100">
          Recorded when an account is made from now on; worked out for older ones: a People import is admin; signing in
          within a minute of the account being made is a self sign-up; an invite the person asked for is a self sign-up;
          inside a confirmed bulk-add batch is admin; anything else, made without anyone signing in, is admin. An override
          below wins over all of it.
        </div>
      </Section>

      <Section title="Bulk-add batches"
        right={<span className="text-[11px] text-grayscale-1100">
          runs of {d.cluster_rule?.min_size}+ accounts made ≤ {d.cluster_rule?.gap_min} min apart · times IST
        </span>}>
        {pending > 0 && (
          <div className="text-xs rounded-lg px-3 py-2 border border-amber-200 bg-amber-50 text-amber-800">
            {pending} run(s) of accounts made minutes apart have not been reviewed: confirm each as a bulk add or say it was not one.
          </div>
        )}
        <DataTable rows={batches.map((b, i) => ({ ...b, when: b.started_at, _i: i }))} maxHeight="none" csvName="bulk_add_batches"
          columns={['when', 'accounts_now', 'signed_in', 'status', 'label', 'note', 'overridden']}
          labels={{ when: 'When (IST)', accounts_now: 'Accounts', signed_in: 'Signed in since', status: 'Status',
            label: 'Label', note: 'Note', overridden: 'Set by hand' }}
          render={{
            when: r => <span className="whitespace-nowrap">{istFmt(r.started_at)}{istFmt(r.started_at, false) !== istFmt(r.ended_at, false) ? `–${istFmt(r.ended_at, false)}` : ''}</span>,
            status: r => <span className={`text-[11px] rounded-full border px-2 py-0.5 whitespace-nowrap ${STATUS[r.status]?.cls || ''}`}>{STATUS[r.status]?.label || r.status}</span>,
            note: r => <span className="text-xs text-grayscale-1100 line-clamp-2" title={r.note}>{r.note || '—'}</span>,
          }}
          actions={r => {
            const b = batches[r._i];
            return (
              <span className="flex flex-wrap gap-1">
                {b.status !== 'confirmed' && <Btn small onClick={() => setStatus(b, 'confirmed')} disabled={busy}>Bulk add</Btn>}
                {b.status !== 'rejected' && <Btn small onClick={() => setStatus(b, 'rejected')} disabled={busy}>Not a bulk add</Btn>}
                {b.id && <Btn small onClick={() => { setEdit(b); setEditLabel(b.label); setEditNote(b.note); }} disabled={busy}>Edit</Btn>}
                {b.id && (
                  <select aria-label="Set this batch's accounts" value="" disabled={busy}
                    onChange={e => { const v = e.target.value; if (v) setBatchOrigin(b, v === 'auto' ? '' : v); }}
                    className="h-7 px-1 rounded-md border-[0.5px] border-primary-300 bg-white text-xs">
                    <option value="">Set accounts…</option>
                    <option value="self">All self sign-up</option>
                    <option value="admin">All added by admin</option>
                    <option value="auto">Back to automatic</option>
                  </select>
                )}
              </span>
            );
          }} />
      </Section>

      <Section title="Set one account's origin">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-xs text-grayscale-1200">E-mail
            <TextInput value={one.email} onChange={v => setOne(s => ({ ...s, email: v }))} placeholder="name@example.com" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-grayscale-1200">Origin
            <Select value={one.origin} onChange={v => setOne(s => ({ ...s, origin: v }))}
              options={['self', 'admin', '']} labels={{ self: 'Self sign-up', admin: 'Added by admin', '': 'Automatic (remove override)' }} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-grayscale-1200 flex-1 min-w-[14rem]">Note (why)
            <TextInput wide value={one.note} onChange={v => setOne(s => ({ ...s, note: v }))} placeholder="e.g. signed up at the webinar; added for them" />
          </label>
          <Btn kind="primary" disabled={busy || !one.email.trim()}
            onClick={async () => {
              if (await post('origin_override', { email: one.email.trim(), origin: one.origin, note: one.note },
                one.origin ? `${one.email.trim()} set to ${ORIGIN_LABEL[one.origin]}.` : `${one.email.trim()} is back to automatic.`)) {
                setOne({ email: '', origin: 'self', note: '' });
              }
            }}>Save</Btn>
        </div>
        {overrides.length > 0 ? (
          <DataTable rows={overrides.map(o => ({ ...o }))} maxHeight="22rem" csvName="account_origin_overrides"
            columns={['email', 'origin', 'note', 'set_by', 'set_at']}
            labels={{ email: 'Account', origin: 'Origin', note: 'Note', set_by: 'Set by', set_at: 'When' }}
            render={{ origin: r => <OriginPill value={r.origin} /> }}
            actions={r => (
              <Btn small disabled={busy} onClick={() => post('origin_override', { user_id: r.user_id, origin: '' },
                `${r.email || r.user_id} is back to automatic.`)}>Remove</Btn>
            )} />
        ) : <div className="text-xs text-grayscale-1100">No account has been set by hand.</div>}
      </Section>

      {edit && (
        <Modal title={`Batch of ${istFmt(edit.started_at)}`} onClose={() => setEdit(null)}
          footer={<>
            <Btn onClick={() => setEdit(null)}>Cancel</Btn>
            <Btn kind="primary" disabled={busy} onClick={async () => {
              if (await post('origin_batch_save', { id: edit.id, status: edit.status === 'detected' ? 'confirmed' : edit.status,
                label: editLabel, note: editNote }, 'Batch saved.')) setEdit(null);
            }}>Save</Btn>
          </>}>
          <label className="flex flex-col gap-1 text-xs text-grayscale-1200">Label
            <TextInput wide value={editLabel} onChange={setEditLabel} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-grayscale-1200">Note
            <textarea value={editNote} onChange={e => setEditNote(e.target.value)} rows={3} maxLength={500}
              className="w-full px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
          </label>
        </Modal>
      )}
    </div>
  );
};

export default OriginAdmin;
